/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

class ProductSingle extends HTMLElement {
    #resizeObserver;
    #variant;
    constructor() {
        super();
        this.updateGallery = this.updateGallery.bind(this);
        this.setPdpHeight = this.setPdpHeight.bind(this);
        this.selectVariantFromThumbnail = this.selectVariantFromThumbnail.bind(this);
    }
    connectedCallback() {
        this.mediaGallery = this.querySelector("media-gallery");
        this.addEventListener("click", this.selectVariantFromThumbnail);
        this.productUrl = this.dataset.productUrl;
        this.sectionId = this.dataset.sectionId;
        this.#moveModalsFromStickyScrollContainer();
        this.#resizeObserver = new ResizeObserver(debounce(this.setPdpHeight, 5));
        this.#resizeObserver.observe(this.querySelector(".product-single__box"));
    }
    disconnectedCallback() {
        this.#resizeObserver.disconnect();
        this.removeEventListener("click", this.selectVariantFromThumbnail);
    }
    // Mental Riders: use Venue's own variant picker when a thumbnail has
    // one unambiguous option value. On multi-option products change colour only.
    selectVariantFromThumbnail(event) {
        const thumbnail = event.target.closest?.("media-gallery-navigation .thumbnail");
        if (!thumbnail || thumbnail.closest("product-single") !== this) return;
        const slideNumber = thumbnail.dataset.slideNumber;
        if (!/^\d+$/.test(slideNumber || "")) return;
        const gallery = thumbnail.closest("media-gallery");
        const media = gallery?.querySelector(`.js-slides [data-slide-id="${slideNumber}"][data-media-id]`);
        if (!media?.classList.contains("media-gallery__item--image")) return;
        const picker = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
        if (!picker || typeof picker.setOptionByValueId !== "function") return;
        const data = picker.querySelector('[id^="all-variant-data-"]');
        let variants;
        try {
            variants = JSON.parse(data?.textContent || "null");
        } catch {
            return;
        }
        if (!Array.isArray(variants) || !variants.length) return;
        const groups = Array.from(picker.querySelectorAll("fieldset, select"));
        if (!groups.length || variants.some(variant => variant.options?.length !== groups.length)) return;
        const groupName = group => group.tagName === "SELECT"
            ? group.name.replace(/^options\[|\]$/g, "")
            : group.querySelector("input[data-option-value-id]")?.name || "";
        const colourGroups = groups.map((group, index) =>
            /^(color|colour)$/i.test(groupName(group).trim()) ? index : -1
        ).filter(index => index >= 0);
        // Never infer a size or other option from a photo. A one-option product
        // can keep using its sole option, whatever that option is called.
        const optionIndex = groups.length === 1 ? 0
            : colourGroups.length === 1 ? colourGroups[0] : -1;
        if (optionIndex < 0) return;
        const matches = variants.filter(variant =>
            String(variant.featured_media?.id) === media.dataset.mediaId);
        const values = new Set(matches.map(variant => variant.options[optionIndex]));
        // Several sizes can share an image, provided they all have the same colour.
        // Unassigned photos and images shared across colours remain browsing-only.
        if (values.size !== 1) return;
        const value = values.values().next().value;
        const controls = Array.from(groups[optionIndex].querySelectorAll("input[data-option-value-id], option[data-option-value-id]"));
        const control = controls.find(element => element.value === value);
        if (!control || control.dataset.productUrl && control.dataset.productUrl !== picker.dataset.url) return;
        // Also cancels an older in-flight selection when returning to the original.
        if (control.tagName === "INPUT" ? control.checked : control.selected) return;
        picker.setOptionByValueId(control.dataset.optionValueId);
    }
    setPdpHeight() {
        this.style.setProperty("--pdp-height", `${this.querySelector(".product-single__box").offsetHeight}px`);
    }
    updateGallery(mediaGallery) {
        const gallery = this.querySelector("media-gallery");
        if (!mediaGallery || !this.sectionId || !this.productUrl || !gallery) return;
        const newGallery = mediaGallery;
        gallery.replaceWith(newGallery);
        this.mediaGallery = newGallery;
        if (window.ShopifyXR) window.ShopifyXR.setupXRElements();
    }
    updateMedia() {
        if (!this.mediaGallery || !this.#variant.featured_media) return;
        const mediaId = this.#variant.featured_media.id;
        this.mediaGallery.goToSlide(mediaId);
    }
    set variant(variant) {
        this.#variant = variant;
        this.updateMedia();
    }
    get variant() {
        return this.#variant;
    }
    #moveModalsFromStickyScrollContainer() {
        const modals = this.querySelectorAll("sticky-scroll modal-dialog, sticky-scroll popup-dialog");
        modals.forEach((modal => {
            this.append(modal);
        }));
    }
}

customElements.define("product-single", ProductSingle);
//# sourceMappingURL=product-single.js.map
